import { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Required } from "utility-types";
import { subDays } from "date-fns";
import { format } from "date-fns-tz";
import DatePicker, { DateObject } from "react-multi-date-picker";

import {
  useCreateDispatchActions,
  ProjectDataDownloadFlowMetadataFetchResponsePayload,
  ProjectDataDownloadFlowRequestResponsePayload,
  useSelectDataDownloadFlowMetadata,
  useSelectTheme,
  useSelectUserDownload,
} from "../../store";
import { RequestResult, t, useCurrentWorkspaceAccountPlan } from "../../lib";
import { IParticipant, IProject } from "../../shared/db";
import { JobState } from "../../shared/queue";
import {
  UserDownload,
  UserDownloadInfo,
  UserDownloadResultError,
  useUserDownload,
  jobDataToUserDownloadInfo,
  ProjectDataDownloadFlowMetadata,
} from "../../model";
import { getProgressBarData, ProgressBarData } from "../../lib/data_download/progress";
import { DataDownloadCalendarIcon } from "../../lib/svg/icon/data_download_calendar";
import { DataDownloadClockIcon } from "../../lib/svg/icon/data_download_clock";
import { DataDownloadCloseIcon } from "../../lib/svg/icon/data_download_close";
import { DataDownloadDownloadIcon } from "../../lib/svg/icon/data_download_download";
import { DataDownloadUsersIcon } from "../../lib/svg/icon/data_download_users";
import { UpgradeContactPop } from "./upgrade_contact_pop";
import { useDataDownloadDesignTokens } from "./data_download_design_tokens";
import {
  ACTIVE_PACKAGE_ID,
  DownloadPackageFileViewModel,
  DownloadPackageViewModel,
  PackageStatus,
  ParticipantDisplay,
  createActivePackage,
  createMetadataPackage,
  getParticipantDisplayList,
} from "./data_download_package_view_model";

const METADATA_FETCH_INTERVAL = 3000;
const HELP_CENTER_URL = "https://help.labfront.com/data-metrics-and-analysis";

enum TimeFrameOption {
  lastSevenDays = "lastSevenDays",
  lastThirtyDays = "lastThirtyDays",
  allTime = "allTime",
  specificPeriod = "specificPeriod",
}

type SelectedPackageId = string | null;

interface ParticipantSelection {
  participant: IParticipant;
  selected: boolean;
}

interface DataDownloadContentProps {
  project: IProject;
  participantList: IParticipant[];
  onClose: () => void;
}

const toUtcDateBoundary = (date: Date): Date => {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const createRelativeTimeFramePeriod = (): Readonly<
  Record<Exclude<TimeFrameOption, TimeFrameOption.specificPeriod>, [Date | null, Date]>
> => {
  const endDay = new Date();
  return {
    [TimeFrameOption.lastSevenDays]: [subDays(endDay, 7), endDay],
    [TimeFrameOption.lastThirtyDays]: [subDays(endDay, 30), endDay],
    [TimeFrameOption.allTime]: [null, endDay],
  };
};

const datePickerValueToDate = (value: unknown): Date | null => {
  let date: Date | undefined;
  if (value instanceof Date) {
    date = value;
  } else if (value instanceof DateObject) {
    date = value.toDate();
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  }
  return date !== undefined && Number.isFinite(date.getTime()) ? date : null;
};

const formatPeriodText = (period: [Date | null, Date | null]): string => {
  const [startDate, endDate] = period;
  if (startDate && endDate) {
    return `${format(startDate, "MMM d, yyyy")} - ${format(endDate, "MMM d, yyyy")}`;
  }
  if (startDate) return `${format(startDate, "MMM d, yyyy")} - Now`;
  if (endDate) return `Through ${format(endDate, "MMM d, yyyy")}`;
  return "All time";
};

const TopBar = (props: { onClose: () => void }) => {
  const { onClose } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <header
      css={{
        height: 56,
        padding: "0 24px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: tokens.surface,
        borderBottom: `1px solid ${tokens.border}`,
        flexShrink: 0,
      }}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        css={{
          width: 28,
          height: 28,
          border: 0,
          borderRadius: tokens.radiusSm,
          display: "grid",
          placeItems: "center",
          background: "transparent",
          color: tokens.muted,
          cursor: "pointer",
          "& svg": {
            stroke: "currentColor",
          },
          ":hover": {
            background: tokens.slateSoft,
            color: tokens.fg,
          },
        }}
      >
        <DataDownloadCloseIcon />
      </button>
      <div
        css={{
          color: tokens.muted,
          fontSize: tokens.fontSize.topbar,
        }}
      >
        Questions about your data?
        <a
          href={HELP_CENTER_URL}
          target="_blank"
          rel="noreferrer"
          css={{
            color: tokens.accent,
            textDecoration: "none",
            marginLeft: 4,
            ":hover": {
              textDecoration: "underline",
            },
          }}
        >
          Visit our Help Center
        </a>
      </div>
    </header>
  );
};

const PrimitiveButton = (props: {
  children: ReactNode;
  variant?: "primary" | "ghost" | "danger";
  size?: "default" | "large" | "compact";
  disabled?: boolean;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  type?: "button" | "submit";
  style?: Record<string, string | number | undefined>;
}) => {
  const {
    children,
    variant = "primary",
    size = "default",
    disabled,
    onClick,
    type = "button",
    style,
  } = props;
  const tokens = useDataDownloadDesignTokens();
  const variantStyle = {
    primary: {
      background: tokens.accent,
      borderColor: tokens.accent,
      color: tokens.surface,
      hoverBackground: tokens.accentHover,
      hoverBorderColor: tokens.accentHover,
    },
    ghost: {
      background: tokens.surface,
      borderColor: tokens.border,
      color: tokens.rowText,
      hoverBackground: tokens.surface,
      hoverBorderColor: tokens.hoverBorder,
    },
    danger: {
      background: tokens.surface,
      borderColor: tokens.dangerBorder,
      color: tokens.danger,
      hoverBackground: tokens.dangerSoft,
      hoverBorderColor: tokens.danger,
    },
  }[variant];
  const padding = size === "large" ? "11px 18px" : size === "compact" ? "6px 10px" : "8px 12px";
  const fontSize = size === "large" ? tokens.fontSize.buttonLarge : tokens.fontSize.button;

  return (
    <button
      type={type}
      disabled={disabled}
      onClick={onClick}
      css={{
        ...style,
        flex: style?.flex ?? 0,
        padding,
        borderRadius: size === "large" ? tokens.radius : tokens.radiusSm,
        fontSize,
        lineHeight: 1.2,
        fontWeight: tokens.fontWeight.bold,
        border: `1px solid ${variantStyle.borderColor}`,
        background: variantStyle.background,
        color: variantStyle.color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        whiteSpace: "nowrap",
        transition: "background 120ms ease, border-color 120ms ease",
        ":hover": disabled
          ? {}
          : {
              background: variantStyle.hoverBackground,
              borderColor: variantStyle.hoverBorderColor,
            },
        ":focus-visible": {
          outline: `2px solid ${tokens.accent}`,
          outlineOffset: 2,
        },
      }}
    >
      {children}
    </button>
  );
};

const StatusPill = (props: { status: PackageStatus }) => {
  const { status } = props;
  const tokens = useDataDownloadDesignTokens();
  const label =
    status === "zipping" ? "In Progress (Zipping)" : status === "ready" ? "Ready" : "Expired";
  const colors = {
    zipping: { color: tokens.amber, bg: tokens.amberSoft },
    ready: { color: tokens.green, bg: tokens.greenSoft },
    expired: { color: tokens.slate, bg: tokens.slateSoft },
  }[status];
  return (
    <span
      css={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: tokens.fontSize.status,
        fontWeight: tokens.fontWeight.bold,
        lineHeight: 1,
        padding: "5px 9px 5px 7px",
        borderRadius: 999,
        letterSpacing: "0.01em",
        color: colors.color,
        background: colors.bg,
      }}
    >
      <span
        css={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: colors.color,
          flexShrink: 0,
        }}
      />
      {label}
    </span>
  );
};

const PackageCard = (props: {
  pkg: DownloadPackageViewModel;
  active: boolean;
  onClick: () => void;
  onCancel: () => void;
}) => {
  const { pkg, active, onClick, onCancel } = props;
  const tokens = useDataDownloadDesignTokens();

  return (
    <article
      data-testid={`download-package-card-${pkg.status}`}
      onClick={onClick}
      css={{
        background: tokens.surface,
        border: `1px solid ${active ? tokens.accent : tokens.border}`,
        borderRadius: tokens.radiusLg,
        padding: "14px 14px 12px",
        cursor: "pointer",
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        boxShadow: active ? tokens.activeRingShadow : undefined,
        ":hover": {
          borderColor: active ? tokens.accent : tokens.hoverBorder,
        },
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          marginBottom: 10,
        }}
      >
        <StatusPill status={pkg.status} />
        {pkg.status === "zipping" ? (
          <PrimitiveButton
            variant="danger"
            size="compact"
            onClick={(event) => {
              event.stopPropagation();
              onCancel();
            }}
          >
            Cancel
          </PrimitiveButton>
        ) : (
          <span
            css={{
              fontSize: tokens.fontSize.cardSub,
              color: tokens.muted,
              fontVariantNumeric: "tabular-nums",
              fontWeight: pkg.status === "expired" ? tokens.fontWeight.medium : undefined,
            }}
          >
            {pkg.sidebarMetaText}
          </span>
        )}
      </div>
      {pkg.status === "zipping" ? (
        <div
          css={{
            height: 4,
            background: tokens.border2,
            borderRadius: 999,
            overflow: "hidden",
            marginTop: 6,
            marginBottom: 10,
          }}
        >
          <span
            css={{
              display: "block",
              height: "100%",
              width: `${pkg.progressBarData?.percentage ?? 0}%`,
              background: tokens.amber,
              borderRadius: 999,
              animation: "data-download-shimmer 2s ease-in-out infinite",
            }}
          />
        </div>
      ) : null}
      <div
        css={{
          display: "grid",
          gap: 7,
          fontSize: tokens.fontSize.cardBody,
          color: tokens.fg,
          marginBottom: pkg.status === "ready" ? 12 : 0,
        }}
      >
        <PackageCardRow
          label="Participants"
          value={pkg.participantsText}
          subValue={pkg.participantSubText}
        />
        <PackageCardRow label="Range" value={pkg.dateRangeText} />
        <PackageCardRow label="Duration" value={pkg.durationText} />
        <PackageCardRow
          label={pkg.status === "zipping" ? "Est. size" : "Size"}
          value={pkg.sizeText}
        />
        <PackageCardRow
          label="Created"
          value={`${pkg.createdDateText}, ${pkg.createdMetaText.split(" by ")[0]}`}
        />
      </div>
    </article>
  );
};

const PackageCardRow = (props: { label: string; value: string; subValue?: string }) => {
  const { label, value, subValue } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      css={{
        display: "flex",
        alignItems: subValue ? "flex-start" : "center",
        gap: 8,
        color: tokens.rowText,
      }}
    >
      <span
        css={{
          color: tokens.muted,
          width: 78,
          flexShrink: 0,
          fontSize: tokens.fontSize.cardLabel,
        }}
      >
        {label}
      </span>
      <span
        css={{
          color: tokens.rowValue,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
        {subValue ? (
          <span
            css={{
              display: "block",
              fontSize: tokens.fontSize.cardSub,
              color: tokens.muted,
              lineHeight: 1.2,
              marginTop: 1,
            }}
          >
            {subValue}
          </span>
        ) : null}
      </span>
    </div>
  );
};

const PackageSidebar = (props: {
  packages: DownloadPackageViewModel[];
  selectedPackageId: SelectedPackageId;
  hasNextPage: boolean;
  nextPage: number | null | undefined;
  disableCreateNewDownload: boolean;
  onSelectNew: () => void;
  onSelectPackage: (id: string) => void;
  onCancelPackage: () => void;
  onLoadMore: (page: number) => void;
}) => {
  const {
    packages,
    selectedPackageId,
    hasNextPage,
    nextPage,
    disableCreateNewDownload,
    onSelectNew,
    onSelectPackage,
    onCancelPackage,
    onLoadMore,
  } = props;
  const tokens = useDataDownloadDesignTokens();

  return (
    <aside
      css={{
        width: 380,
        borderRight: `1px solid ${tokens.border}`,
        background: tokens.surface,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        flexShrink: 0,
      }}
    >
      <div
        css={{
          padding: 16,
          borderBottom: `1px solid ${tokens.border2}`,
        }}
      >
        <button
          type="button"
          disabled={disableCreateNewDownload}
          onClick={disableCreateNewDownload ? undefined : onSelectNew}
          css={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            width: "100%",
            padding: "16px 18px",
            border: `1px dashed ${tokens.accent}`,
            background: tokens.accentSoft,
            color: tokens.accent,
            borderRadius: 12,
            fontSize: tokens.fontSize.newButton,
            lineHeight: 1.2,
            fontWeight: tokens.fontWeight.bold,
            cursor: disableCreateNewDownload ? "not-allowed" : "pointer",
            textAlign: "left",
            opacity: disableCreateNewDownload ? 0.55 : 1,
            transition: "background 120ms ease",
            boxShadow: selectedPackageId === null ? tokens.activeRingShadow : undefined,
            ":hover": disableCreateNewDownload
              ? {}
              : {
                  background: tokens.newButtonHover,
                },
          }}
        >
          <span
            css={{
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              width: 36,
              height: 36,
              borderRadius: tokens.radius,
              background: tokens.surface,
              color: tokens.accent,
              fontSize: tokens.fontSize.newButtonIcon,
              fontWeight: tokens.fontWeight.regular,
              lineHeight: 1,
            }}
          >
            +
          </span>
          <span>
            Create a new download
            <span
              css={{
                display: "block",
                fontWeight: tokens.fontWeight.regular,
                fontSize: tokens.fontSize.newButtonSub,
                color: tokens.accent,
                opacity: 0.75,
                marginTop: 3,
              }}
            >
              Choose participants & date range
            </span>
          </span>
        </button>
      </div>
      <div
        css={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          padding: "18px 20px 10px",
        }}
      >
        <h2
          css={{
            margin: 0,
            fontSize: tokens.fontSize.sidebarTitle,
            fontWeight: tokens.fontWeight.bold,
            letterSpacing: "-0.005em",
          }}
        >
          All packages
        </h2>
        <span
          css={{
            color: tokens.muted,
            fontSize: tokens.fontSize.sidebarMeta,
          }}
        >
          {packages.length} package{packages.length === 1 ? "" : "s"}
        </span>
      </div>
      <div
        css={{
          flex: 1,
          overflowY: "auto",
          padding: "4px 16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
          "::-webkit-scrollbar": {
            width: 8,
          },
          "::-webkit-scrollbar-thumb": {
            background: tokens.scrollbarThumb,
            borderRadius: 4,
          },
        }}
      >
        {packages.map((pkg) => (
          <PackageCard
            key={pkg.id}
            pkg={pkg}
            active={selectedPackageId === pkg.id}
            onClick={() => onSelectPackage(pkg.id)}
            onCancel={onCancelPackage}
          />
        ))}
        {!packages.length ? (
          <div
            css={{
              border: `1px solid ${tokens.border}`,
              borderRadius: tokens.radiusLg,
              padding: 16,
              color: tokens.muted,
              fontSize: tokens.fontSize.emptyState,
            }}
          >
            No packages yet.
          </div>
        ) : null}
        {hasNextPage && nextPage !== null && nextPage !== undefined ? (
          <PrimitiveButton
            variant="ghost"
            style={{ width: "100%", flex: 0 }}
            onClick={() => onLoadMore(nextPage)}
          >
            Load more
          </PrimitiveButton>
        ) : null}
      </div>
    </aside>
  );
};

const SectionHeader = (props: { icon: ReactNode; title: string; meta: string }) => {
  const { icon, title, meta } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      css={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 14,
      }}
    >
      <span
        css={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: tokens.fontSize.sectionTitle,
          fontWeight: tokens.fontWeight.bold,
          color: tokens.rowText,
          "& svg": {
            width: 14,
            height: 14,
            color: tokens.muted,
            stroke: tokens.muted,
          },
        }}
      >
        {icon}
        {title}
      </span>
      <span
        css={{
          fontSize: tokens.fontSize.sectionMeta,
          color: tokens.muted,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {meta}
      </span>
    </div>
  );
};

const ParticipantSelector = (props: {
  participantSelectionList: ParticipantSelection[];
  onChange: (participantSelectionList: ParticipantSelection[]) => void;
}) => {
  const { participantSelectionList, onChange } = props;
  const tokens = useDataDownloadDesignTokens();
  const selectedList = participantSelectionList.filter(({ selected }) => selected);
  const selectedAll =
    participantSelectionList.length > 0 && participantSelectionList.length === selectedList.length;

  const setAllSelected = (selected: boolean) => {
    onChange(participantSelectionList.map(({ participant }) => ({ participant, selected })));
  };

  const setParticipantSelected = (participantId: string, selected: boolean) => {
    onChange(
      participantSelectionList.map((entry) =>
        entry.participant.id === participantId
          ? {
              ...entry,
              selected,
            }
          : entry,
      ),
    );
  };

  return (
    <div
      css={{
        padding: "18px 20px",
      }}
    >
      <SectionHeader
        icon={
          <DataDownloadUsersIcon
            stroke={tokens.muted}
            style={{
              color: tokens.muted,
              flexShrink: 0,
            }}
          />
        }
        title="Participants"
        meta={`${selectedList.length} selected`}
      />
      <div
        css={{
          height: 380,
          overflowY: "auto",
          borderTop: `1px solid ${tokens.border2}`,
          margin: "0 -20px",
          padding: "0 20px",
          "::-webkit-scrollbar": {
            width: 8,
          },
          "::-webkit-scrollbar-thumb": {
            background: tokens.scrollbarThumb,
            borderRadius: 4,
          },
        }}
      >
        <ParticipantRow
          label="All participants"
          selected={selectedAll}
          all
          onChange={() => setAllSelected(!selectedAll)}
        />
        {participantSelectionList.map(({ participant, selected }) => (
          <ParticipantRow
            key={participant.id}
            label={participant.insignia ?? participant.id}
            selected={selected}
            onChange={() => setParticipantSelected(participant.id, !selected)}
          />
        ))}
      </div>
    </div>
  );
};

const ParticipantRow = (props: {
  label: string;
  selected: boolean;
  all?: boolean;
  onChange: () => void;
}) => {
  const { label, selected, all, onChange } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <button
      type="button"
      onClick={onChange}
      css={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 0",
        border: 0,
        borderBottom: `1px solid ${tokens.border2}`,
        background: "transparent",
        cursor: "pointer",
        fontSize: tokens.fontSize.participantRow,
        fontWeight: all ? tokens.fontWeight.medium : tokens.fontWeight.regular,
        color: tokens.fg,
        textAlign: "left",
      }}
    >
      <span
        css={{
          width: 16,
          height: 16,
          borderRadius: 4,
          border: `1.5px solid ${selected ? tokens.accent : tokens.hoverBorder}`,
          display: "grid",
          placeItems: "center",
          background: selected ? tokens.accent : tokens.surface,
          flexShrink: 0,
          ":after": selected
            ? {
                content: '""',
                width: 8,
                height: 4,
                borderLeft: `2px solid ${tokens.surface}`,
                borderBottom: `2px solid ${tokens.surface}`,
                transform: "rotate(-45deg) translate(1px, -1px)",
              }
            : undefined,
        }}
      />
      <span
        css={{
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {label}
      </span>
    </button>
  );
};

const TimeFrameSelector = (props: {
  canAccessPurchasedContent: boolean;
  selectedTimeFrame: TimeFrameOption;
  selectedPeriod: [Date | null, Date | null] | null;
  relativeTimeFramePeriod: Readonly<
    Record<Exclude<TimeFrameOption, TimeFrameOption.specificPeriod>, [Date | null, Date]>
  >;
  onTimeFrameSelect: (timeFrame: TimeFrameOption) => void;
  onPeriodChange: (period: [Date | null, Date | null]) => void;
  onUpgradeCheck: () => void;
}) => {
  const {
    canAccessPurchasedContent,
    selectedTimeFrame,
    selectedPeriod,
    relativeTimeFramePeriod,
    onTimeFrameSelect,
    onPeriodChange,
    onUpgradeCheck,
  } = props;
  const tokens = useDataDownloadDesignTokens();
  const selectedPeriodText = selectedPeriod ? formatPeriodText(selectedPeriod) : "Select dates";
  const getMeta = () => {
    if (selectedTimeFrame === TimeFrameOption.specificPeriod) return selectedPeriodText;
    return formatPeriodText(relativeTimeFramePeriod[selectedTimeFrame]);
  };

  const handleSelect = (timeFrame: TimeFrameOption) => {
    const needsUpgrade =
      !canAccessPurchasedContent &&
      (timeFrame === TimeFrameOption.allTime || timeFrame === TimeFrameOption.specificPeriod);
    if (needsUpgrade) {
      onUpgradeCheck();
      return;
    }
    onTimeFrameSelect(timeFrame);
  };

  return (
    <div
      css={{
        padding: "18px 20px",
      }}
    >
      <SectionHeader
        icon={
          <DataDownloadCalendarIcon
            stroke={tokens.muted}
            style={{
              color: tokens.muted,
              flexShrink: 0,
            }}
          />
        }
        title="Timeframe"
        meta={getMeta()}
      />
      <div
        css={{
          display: "grid",
          gap: 8,
          marginTop: 4,
        }}
      >
        <TimeFrameOptionView
          title="Last 7 days"
          subtitle={formatPeriodText(relativeTimeFramePeriod[TimeFrameOption.lastSevenDays])}
          selected={selectedTimeFrame === TimeFrameOption.lastSevenDays}
          onClick={() => handleSelect(TimeFrameOption.lastSevenDays)}
        />
        <TimeFrameOptionView
          title="Last 30 days"
          subtitle={formatPeriodText(relativeTimeFramePeriod[TimeFrameOption.lastThirtyDays])}
          selected={selectedTimeFrame === TimeFrameOption.lastThirtyDays}
          onClick={() => handleSelect(TimeFrameOption.lastThirtyDays)}
        />
        <TimeFrameOptionView
          title="All time"
          subtitle={formatPeriodText(relativeTimeFramePeriod[TimeFrameOption.allTime])}
          selected={selectedTimeFrame === TimeFrameOption.allTime}
          disabled={!canAccessPurchasedContent}
          onClick={() => handleSelect(TimeFrameOption.allTime)}
        />
        <div
          onClick={() => handleSelect(TimeFrameOption.specificPeriod)}
          css={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            padding: "12px 14px",
            border: `1px solid ${selectedTimeFrame === TimeFrameOption.specificPeriod ? tokens.accent : tokens.border}`,
            borderRadius: tokens.radius,
            cursor: "pointer",
            background:
              selectedTimeFrame === TimeFrameOption.specificPeriod
                ? tokens.accentSoft
                : tokens.surface,
            opacity: !canAccessPurchasedContent ? 0.6 : 1,
            transition: "border-color 120ms ease, background 120ms ease",
            ":hover": {
              borderColor:
                selectedTimeFrame === TimeFrameOption.specificPeriod
                  ? tokens.accent
                  : tokens.hoverBorder,
            },
          }}
        >
          <RadioMark selected={selectedTimeFrame === TimeFrameOption.specificPeriod} />
          <div
            css={{
              flex: 1,
            }}
          >
            <div
              css={{
                fontSize: tokens.fontSize.timeOptionTitle,
                fontWeight: tokens.fontWeight.medium,
                color: tokens.fg,
              }}
            >
              Specific period
            </div>
            <div
              onClick={(event) => event.stopPropagation()}
              css={{
                marginTop: 8,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "7px 10px",
                border: `1px solid ${tokens.border}`,
                borderRadius: tokens.radiusSm,
                background: tokens.surface,
                fontSize: tokens.fontSize.dateField,
                color: tokens.rowText,
                fontVariantNumeric: "tabular-nums",
                minWidth: 280,
                "& svg": {
                  width: 13,
                  height: 13,
                  color: tokens.muted,
                  stroke: tokens.muted,
                },
              }}
            >
              <DatePicker
                value={
                  selectedPeriod?.[0] && selectedPeriod?.[1]
                    ? [selectedPeriod[0], selectedPeriod[1]]
                    : undefined
                }
                onChange={(value: unknown) => {
                  handleSelect(TimeFrameOption.specificPeriod);
                  if (!value) {
                    onPeriodChange([null, null]);
                    return;
                  }
                  const valueArray = Array.isArray(value) ? value : [value];
                  if (valueArray.length < 2) return;
                  const start = datePickerValueToDate(valueArray[0]);
                  const end = datePickerValueToDate(valueArray[1]);
                  if (start && end) {
                    onPeriodChange([start, end]);
                  }
                }}
                range
                rangeHover
                format="MMM D, YYYY"
                maxDate={new Date()}
                disabled={
                  !canAccessPurchasedContent || selectedTimeFrame !== TimeFrameOption.specificPeriod
                }
                style={{
                  width: "238px",
                  border: "none",
                  padding: 0,
                  height: "24px",
                  color: tokens.rowText,
                  fontSize: `${tokens.fontSize.dateField}px`,
                  background: "transparent",
                }}
              />
              <DataDownloadCalendarIcon
                stroke={tokens.muted}
                style={{
                  color: tokens.muted,
                  flexShrink: 0,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TimeFrameOptionView = (props: {
  title: string;
  subtitle: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
}) => {
  const { title, subtitle, selected, disabled, onClick } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      css={{
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
        padding: "12px 14px",
        border: `1px solid ${selected ? tokens.accent : tokens.border}`,
        borderRadius: tokens.radius,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        background: selected ? tokens.accentSoft : tokens.surface,
        transition: "border-color 120ms ease, background 120ms ease",
        textAlign: "left",
        ":hover": {
          borderColor: selected ? tokens.accent : tokens.hoverBorder,
        },
      }}
    >
      <RadioMark selected={selected} />
      <span
        css={{
          flex: 1,
        }}
      >
        <span
          css={{
            display: "block",
            fontSize: tokens.fontSize.timeOptionTitle,
            fontWeight: tokens.fontWeight.medium,
            color: tokens.fg,
          }}
        >
          {title}
        </span>
        <span
          css={{
            display: "block",
            fontSize: tokens.fontSize.timeOptionSub,
            color: tokens.muted,
            fontVariantNumeric: "tabular-nums",
            marginTop: 2,
          }}
        >
          {subtitle}
        </span>
      </span>
    </button>
  );
};

const RadioMark = (props: { selected: boolean }) => {
  const { selected } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <span
      css={{
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: `1.5px solid ${selected ? tokens.accent : tokens.hoverBorder}`,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        marginTop: 1,
        background: tokens.surface,
      }}
    >
      {selected ? (
        <span
          css={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: tokens.accent,
          }}
        />
      ) : null}
    </span>
  );
};

const CreateDownloadView = (props: {
  participantSelectionList: ParticipantSelection[];
  canAccessPurchasedContent: boolean;
  selectedTimeFrame: TimeFrameOption;
  selectedPeriod: [Date | null, Date | null] | null;
  relativeTimeFramePeriod: Readonly<
    Record<Exclude<TimeFrameOption, TimeFrameOption.specificPeriod>, [Date | null, Date]>
  >;
  canCreatePackage: boolean;
  onParticipantSelectionChange: (participantSelectionList: ParticipantSelection[]) => void;
  onTimeFrameSelect: (timeFrame: TimeFrameOption) => void;
  onPeriodChange: (period: [Date | null, Date | null]) => void;
  onCreatePackage: () => void;
  onUpgradeCheck: () => void;
}) => {
  const {
    participantSelectionList,
    canAccessPurchasedContent,
    selectedTimeFrame,
    selectedPeriod,
    relativeTimeFramePeriod,
    canCreatePackage,
    onParticipantSelectionChange,
    onTimeFrameSelect,
    onPeriodChange,
    onCreatePackage,
    onUpgradeCheck,
  } = props;
  const tokens = useDataDownloadDesignTokens();

  return (
    <section>
      <PaneHead
        title="Create a new download"
        subtitle="Select participants and a timeframe. We'll zip the package and email you when it's ready."
      />
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 0,
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radiusLg,
          overflow: "hidden",
        }}
      >
        <ParticipantSelector
          participantSelectionList={participantSelectionList}
          onChange={onParticipantSelectionChange}
        />
        <div
          css={{
            borderLeft: `1px solid ${tokens.border2}`,
          }}
        >
          <TimeFrameSelector
            canAccessPurchasedContent={canAccessPurchasedContent}
            selectedTimeFrame={selectedTimeFrame}
            selectedPeriod={selectedPeriod}
            relativeTimeFramePeriod={relativeTimeFramePeriod}
            onTimeFrameSelect={onTimeFrameSelect}
            onPeriodChange={onPeriodChange}
            onUpgradeCheck={onUpgradeCheck}
          />
        </div>
      </div>
      <div
        css={{
          marginTop: 20,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <span
          css={{
            color: tokens.muted,
            fontSize: tokens.fontSize.sectionTitle,
          }}
        >
          We'll email you when the package is ready.
        </span>
        <PrimitiveButton
          size="large"
          disabled={!canCreatePackage}
          onClick={onCreatePackage}
          style={{ flex: 1 }}
        >
          <DataDownloadDownloadIcon
            fill="none"
            stroke={tokens.surface}
            style={{
              color: tokens.surface,
              flexShrink: 0,
            }}
          />
          Create package
        </PrimitiveButton>
      </div>
    </section>
  );
};

const PaneHead = (props: { title: string; subtitle: string; action?: ReactNode }) => {
  const { title, subtitle, action } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      css={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        marginBottom: 24,
        gap: 20,
      }}
    >
      <div>
        <h1
          css={{
            margin: "0 0 6px",
            fontSize: tokens.fontSize.paneTitle,
            fontWeight: tokens.fontWeight.bold,
            letterSpacing: "-0.015em",
            textAlign: "left",
          }}
        >
          {title}
        </h1>
        <p
          css={{
            margin: 0,
            color: tokens.muted,
            fontSize: tokens.fontSize.paneSubtitle,
            textAlign: "left",
          }}
        >
          {subtitle}
        </p>
      </div>
      {action}
    </div>
  );
};

const PackageDetailView = (props: {
  pkg: DownloadPackageViewModel;
  participantDisplayList: ParticipantDisplay[];
}) => {
  const { pkg, participantDisplayList } = props;
  const tokens = useDataDownloadDesignTokens();
  const isExpired = pkg.status === "expired";

  return (
    <section>
      <PaneHead title={pkg.title} subtitle={pkg.subtitle} />
      <div
        css={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radiusLg,
          overflow: "hidden",
        }}
      >
        <div
          css={{
            padding: "22px 24px 18px",
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1fr 1fr",
            gap: 28,
            borderBottom: `1px solid ${tokens.border2}`,
          }}
        >
          <StatBlock
            label="Participants"
            value={pkg.participantsText}
            subValue={pkg.participantSubText}
          />
          <StatBlock label="Date range" value={pkg.dateRangeText} subValue={pkg.durationText} />
          <StatBlock label="Total size" value={pkg.sizeText} subValue={pkg.fileCountText} />
          <StatBlock label="Created" value={pkg.createdDateText} subValue={pkg.createdMetaText} />
        </div>
        <div
          css={{
            padding: "18px 24px",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 10,
            }}
          >
            <h3
              css={{
                margin: 0,
                fontSize: tokens.fontSize.fileHeading,
                fontWeight: tokens.fontWeight.bold,
                color: tokens.rowText,
              }}
            >
              Files in this package
            </h3>
          </div>
          {pkg.files.length ? (
            pkg.files.map((file, index) => (
              <FileRow key={`${file.url}-${index}`} file={file} hideDownload={isExpired} />
            ))
          ) : (
            <div
              css={{
                padding: "11px 0",
                color: tokens.muted,
                fontSize: tokens.fontSize.fileRow,
              }}
            >
              {isExpired ? "Files are no longer available." : "No downloadable files available."}
            </div>
          )}
        </div>
        <div
          css={{
            padding: "18px 24px",
            background: tokens.surface2,
            borderTop: `1px solid ${tokens.border2}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
          }}
        >
          <span
            css={{
              fontSize: tokens.fontSize.expiry,
              color: tokens.muted,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              "& svg": {
                width: 13,
                height: 13,
                color: tokens.amber,
                stroke: tokens.amber,
              },
            }}
          >
            <DataDownloadClockIcon
              stroke={tokens.amber}
              style={{
                color: tokens.amber,
                flexShrink: 0,
              }}
            />
            {pkg.expiryText}
          </span>
        </div>
      </div>
      <ParticipantPillStrip participantDisplayList={participantDisplayList} />
    </section>
  );
};

const FileRow = (props: { file: DownloadPackageFileViewModel; hideDownload: boolean }) => {
  const { file, hideDownload } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      css={{
        display: "grid",
        gridTemplateColumns: "24px 1fr auto auto",
        gap: 14,
        alignItems: "center",
        padding: "11px 0",
        borderBottom: `1px solid ${tokens.border2}`,
        fontSize: tokens.fontSize.fileRow,
        ":last-of-type": {
          borderBottom: 0,
        },
      }}
    >
      <div
        css={{
          width: 24,
          height: 24,
          borderRadius: 5,
          background: tokens.accentSoft,
          display: "grid",
          placeItems: "center",
          color: tokens.accent,
          "& svg": {
            width: 13,
            height: 13,
            color: tokens.accent,
            stroke: tokens.accent,
          },
        }}
      >
        <DataDownloadDownloadIcon
          stroke={tokens.accent}
          style={{
            color: tokens.accent,
            flexShrink: 0,
          }}
        />
      </div>
      <div>
        <div
          css={{
            color: tokens.fg,
            fontWeight: tokens.fontWeight.medium,
          }}
        >
          {file.name}
        </div>
        <div
          css={{
            color: tokens.muted,
            fontSize: tokens.fontSize.fileMeta,
            marginTop: 2,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {file.participantCount} participant{file.participantCount === 1 ? "" : "s"}
        </div>
      </div>
      <div
        css={{
          color: tokens.rowText,
          fontSize: tokens.fontSize.fileSize,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {file.sizeText}
      </div>
      {hideDownload ? null : (
        <a href={file.url} target="_self">
          <PrimitiveButton size="compact">Download</PrimitiveButton>
        </a>
      )}
    </div>
  );
};

const StatBlock = (props: { label: string; value: string; subValue: string }) => {
  const { label, value, subValue } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div>
      <div
        css={{
          fontSize: tokens.fontSize.statLabel,
          color: tokens.muted,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        css={{
          fontSize: tokens.fontSize.statValue,
          fontWeight: tokens.fontWeight.bold,
          letterSpacing: "-0.01em",
          color: tokens.fg,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
      <div
        css={{
          marginTop: 2,
          fontSize: tokens.fontSize.statSub,
          color: tokens.muted,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {subValue}
      </div>
    </div>
  );
};

const ParticipantPillStrip = (props: { participantDisplayList: ParticipantDisplay[] }) => {
  const { participantDisplayList } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      css={{
        marginTop: 20,
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: tokens.radiusLg,
        padding: "18px 24px",
      }}
    >
      <h3
        css={{
          margin: "0 0 12px",
          fontSize: tokens.fontSize.participantPillTitle,
          fontWeight: tokens.fontWeight.bold,
          color: tokens.rowText,
        }}
      >
        Included participants{" "}
        <span
          css={{
            color: tokens.muted,
            fontWeight: tokens.fontWeight.regular,
          }}
        >
          ({participantDisplayList.length})
        </span>
      </h3>
      <div
        css={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
        }}
      >
        {participantDisplayList.map(({ id, label }) => (
          <span
            key={id}
            css={{
              fontSize: tokens.fontSize.participantPill,
              lineHeight: 1,
              fontWeight: tokens.fontWeight.medium,
              color: tokens.rowText,
              background: tokens.surface2,
              border: `1px solid ${tokens.border2}`,
              borderRadius: 4,
              padding: "5px 8px",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.01em",
            }}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};

const ZippingDetailView = (props: {
  pkg: DownloadPackageViewModel;
  participantDisplayList: ParticipantDisplay[];
  onCancel: () => void;
}) => {
  const { pkg, participantDisplayList, onCancel } = props;
  const tokens = useDataDownloadDesignTokens();
  const percentage = pkg.progressBarData?.percentage ?? 0;
  const bytesProcessed = pkg.progressBarData?.bytesProcessed;
  const bytesTotal = pkg.progressBarData?.bytesTotal;
  const remaining = pkg.progressBarData
    ? t.formatDuration(pkg.progressBarData.estimatedTimeRemainingInMs)
    : undefined;

  return (
    <section>
      <PaneHead
        title="Download in progress"
        subtitle="Compressing your files — this can take a few hours. You can leave this page; we'll email you when ready."
        action={
          <PrimitiveButton variant="danger" size="large" onClick={onCancel}>
            <DataDownloadCloseIcon
              stroke={tokens.danger}
              style={{
                color: tokens.danger,
                flexShrink: 0,
              }}
            />
            Cancel build
          </PrimitiveButton>
        }
      />
      <div
        css={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: tokens.radiusLg,
          overflow: "hidden",
        }}
      >
        <div
          css={{
            background: tokens.zippingPanelBackground,
            borderBottom: `1px solid ${tokens.zippingPanelBorder}`,
            padding: "22px 24px 20px",
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 16,
            }}
          >
            <div
              css={{
                display: "flex",
                alignItems: "center",
                gap: 12,
              }}
            >
              <div
                css={{
                  width: 22,
                  height: 22,
                  borderRadius: "50%",
                  border: `2.5px solid ${tokens.zippingRing}`,
                  borderTopColor: tokens.amber,
                  animation: "data-download-spin 1s linear infinite",
                  flexShrink: 0,
                }}
              />
              <div>
                <div
                  css={{
                    fontSize: tokens.fontSize.zippingTitle,
                    fontWeight: tokens.fontWeight.bold,
                    color: tokens.fg,
                  }}
                >
                  Building your download
                </div>
                <div
                  css={{
                    fontSize: tokens.fontSize.zippingSubtitle,
                    color: tokens.muted,
                    marginTop: 2,
                  }}
                >
                  Your files are being compressed. This may take a while.
                </div>
              </div>
            </div>
            <div
              css={{
                fontSize: tokens.fontSize.zippingPercent,
                fontWeight: tokens.fontWeight.thick,
                color: tokens.amber,
                letterSpacing: "-0.03em",
                fontVariantNumeric: "tabular-nums",
                lineHeight: 1,
              }}
            >
              {percentage}
              <sup
                css={{
                  fontSize: tokens.fontSize.zippingPercentSup,
                  fontWeight: tokens.fontWeight.bold,
                  verticalAlign: "super",
                  lineHeight: 1,
                }}
              >
                %
              </sup>
            </div>
          </div>
          <div
            css={{
              height: 8,
              background: tokens.zippingProgressBackground,
              borderRadius: 999,
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            <div
              css={{
                height: "100%",
                width: `${percentage}%`,
                borderRadius: 999,
                background: tokens.zippingProgressGradient,
                animation: "data-download-shimmer 2s ease-in-out infinite",
              }}
            />
          </div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: tokens.fontSize.fileSize,
              color: tokens.rowText,
              fontVariantNumeric: "tabular-nums",
              marginTop: 6,
            }}
          >
            <span>
              <strong>{bytesProcessed ? t.formatBytes(bytesProcessed) : `${percentage}%`}</strong>
              {bytesTotal ? ` of ~${t.formatBytes(bytesTotal)} compressed` : " compressed"}
            </span>
            <span
              css={{
                color: tokens.zippingRemaining,
                fontWeight: tokens.fontWeight.bold,
              }}
            >
              {remaining ? `~${remaining} remaining` : "Calculating time remaining"}
            </span>
          </div>
        </div>
        <div
          css={{
            padding: "20px 24px",
            display: "grid",
            gridTemplateColumns: "1.2fr 1fr 1fr",
            gap: 28,
            borderBottom: `1px solid ${tokens.border2}`,
          }}
        >
          <StatBlock
            label="Participants"
            value={pkg.participantsText}
            subValue={pkg.participantSubText}
          />
          <StatBlock label="Date range" value={pkg.dateRangeText} subValue={pkg.durationText} />
          <StatBlock label="Created" value={pkg.createdDateText} subValue={pkg.createdMetaText} />
        </div>
        <div
          css={{
            padding: "14px 24px",
            background: tokens.surface2,
            borderTop: `1px solid ${tokens.border2}`,
          }}
        >
          <span
            css={{
              fontSize: tokens.fontSize.expiry,
              color: tokens.muted,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              "& svg": {
                width: 13,
                height: 13,
                color: tokens.amber,
                stroke: tokens.amber,
              },
            }}
          >
            <DataDownloadClockIcon
              stroke={tokens.amber}
              style={{
                color: tokens.amber,
                flexShrink: 0,
              }}
            />
            Available for 30 days once ready
          </span>
        </div>
      </div>
      <ParticipantPillStrip participantDisplayList={participantDisplayList} />
    </section>
  );
};

const CancelBuildDialog = (props: { onDismiss: () => void; onConfirm: () => void }) => {
  const { onDismiss, onConfirm } = props;
  const tokens = useDataDownloadDesignTokens();
  return (
    <div
      onClick={onDismiss}
      css={{
        position: "fixed",
        inset: 0,
        background: tokens.dialogOverlay,
        zIndex: 200,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        css={{
          background: tokens.surface,
          border: `1px solid ${tokens.border}`,
          borderRadius: 12,
          padding: "28px 28px 24px",
          width: 380,
          boxShadow: tokens.dialogShadow,
        }}
      >
        <h2
          css={{
            margin: "0 0 8px",
            fontSize: tokens.fontSize.dialogTitle,
            fontWeight: tokens.fontWeight.bold,
            letterSpacing: "-0.01em",
            color: tokens.fg,
          }}
        >
          Cancel this download?
        </h2>
        <p
          css={{
            margin: "0 0 24px",
            fontSize: tokens.fontSize.dialogBody,
            color: tokens.muted,
            lineHeight: 1.55,
          }}
        >
          The build will stop and any progress will be lost. You can always start a new download
          from the packages list.
        </p>
        <div
          css={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <PrimitiveButton variant="ghost" onClick={onDismiss}>
            Keep building
          </PrimitiveButton>
          <PrimitiveButton
            onClick={onConfirm}
            style={{
              background: tokens.danger,
              borderColor: tokens.danger,
            }}
          >
            Yes, cancel
          </PrimitiveButton>
        </div>
      </div>
    </div>
  );
};

const DownloadErrorDialog = (props: { onClose: () => void }) => {
  const { onClose } = props;
  const { color, borderRadius, fontSize, fontWeight } = useSelectTheme();

  return (
    <div
      css={{
        position: "fixed",
        inset: 0,
        zIndex: 210,
        backgroundColor: color.popupBackground,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        css={{
          width: 640,
          background: color.surface.white,
          borderRadius: borderRadius.popup,
          overflow: "hidden",
        }}
      >
        <div
          css={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            padding: 24,
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <p
              css={{
                fontWeight: fontWeight.thick,
                fontSize: fontSize.h4,
                color: color.textIcon.title,
              }}
            >
              Unable to complete data download
            </p>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              css={{
                width: 32,
                height: 32,
                border: 0,
                background: "transparent",
                color: color.textIcon.secondary,
                cursor: "pointer",
              }}
            >
              <DataDownloadCloseIcon />
            </button>
          </div>
          <p
            css={{
              fontSize: fontSize.h6,
              fontWeight: fontWeight.medium,
              color: color.textIcon.secondary,
              lineHeight: "24px",
            }}
          >
            We encountered an issue while generating your export. Please refresh your browser and
            try again. If the file is exceptionally large (e.g., high-resolution BBI or raw
            accelerometry), ensure you have a stable internet connection or try selecting a smaller
            date range for this Participant group.
          </p>
        </div>
        <div
          css={{
            display: "flex",
            justifyContent: "flex-end",
            padding: "12px 24px",
            borderTop: `1px solid ${color.border.light}`,
          }}
        >
          <PrimitiveButton variant="ghost" onClick={onClose}>
            Close
          </PrimitiveButton>
        </div>
      </div>
    </div>
  );
};

export const DataDownloadContent = (props: DataDownloadContentProps) => {
  const { project, participantList, onClose } = props;
  const tokens = useDataDownloadDesignTokens();
  const projectId = project.id;

  const {
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH,
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_LIST_FETCH,
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_REQUEST,
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_CANCEL,
    doUSER_DOWNLOAD_SET,
    doUSER_DOWNLOAD_DELETE,
  } = useCreateDispatchActions();

  const { hasActivePlanPurchase } = useCurrentWorkspaceAccountPlan();
  const canAccessPurchasedContent = hasActivePlanPurchase ?? false;
  const relativeTimeFramePeriod = useMemo(() => createRelativeTimeFramePeriod(), []);

  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [showDownloadErrorPopup, setShowDownloadErrorPopup] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<SelectedPackageId>(null);
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(TimeFrameOption.lastSevenDays);
  const [selectedPeriod, setSelectedPeriod] = useState<[Date | null, Date | null] | null>(
    relativeTimeFramePeriod[TimeFrameOption.lastSevenDays],
  );
  const [participantSelectionList, setParticipantSelectionList] = useState<ParticipantSelection[]>(
    () =>
      participantList.map((participant) => ({
        participant,
        selected: true,
      })),
  );

  const [
    requestResultOfProjectDataDownloadFlowMetadataFetch,
    setRequestResultOfProjectDataDownloadFlowMetadataFetch,
  ] = useState<RequestResult | null>(null);
  const [
    requestResultOfProjectDataDownloadFlowRequest,
    setRequestResultOfProjectDataDownloadFlowRequest,
  ] = useState<RequestResult | null>(null);
  const [
    requestResultOfProjectDataDownloadFlowCancel,
    setRequestResultOfProjectDataDownloadFlowCancel,
  ] = useState<RequestResult | null>(null);
  const [hasCheckedActiveDownload, setHasCheckedActiveDownload] = useState(false);
  const [isDownloadFlowRequestPending, setIsDownloadFlowRequestPending] = useState(false);

  const metadataFetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAutoSelectedActiveRef = useRef(false);

  const participantById = useMemo(() => {
    return new Map(participantList.map((participant) => [participant.id, participant]));
  }, [participantList]);

  useEffect(() => {
    setParticipantSelectionList((current) => {
      const currentSelectionById = new Map(
        current.map(({ participant, selected }) => [participant.id, selected]),
      );
      return participantList.map((participant) => ({
        participant,
        selected: currentSelectionById.get(participant.id) ?? true,
      }));
    });
  }, [participantList]);

  const { userDownloadInfo, isUserDownloadInProgress, errorUserDownloadResult, userDownload } =
    useUserDownload(useSelectUserDownload()?.[projectId]);
  const progressBarData: ProgressBarData | null = useMemo(() => {
    if (!isUserDownloadInProgress) return null;
    return getProgressBarData(userDownload);
  }, [userDownload, isUserDownloadInProgress]);

  const metadataProjectState = useSelectDataDownloadFlowMetadata()?.[projectId];
  const metadataList = useMemo(() => {
    if (!metadataProjectState) return [];
    return metadataProjectState.orderedIds
      .map((id) => metadataProjectState.byId[id])
      .filter((metadata): metadata is ProjectDataDownloadFlowMetadata => !!metadata);
  }, [metadataProjectState]);
  const metadataPage = metadataProjectState?.page ?? null;

  const activePackage = useMemo(() => {
    return createActivePackage(userDownload, progressBarData, participantById);
  }, [participantById, progressBarData, userDownload]);
  const isDownloadStartBlocked =
    !hasCheckedActiveDownload ||
    isDownloadFlowRequestPending ||
    isUserDownloadInProgress ||
    !!activePackage;
  const metadataPackages = useMemo(() => {
    return metadataList
      .map((metadata) => createMetadataPackage(metadata, participantById))
      .filter((pkg): pkg is DownloadPackageViewModel => !!pkg);
  }, [metadataList, participantById]);
  const packages = useMemo(() => {
    return activePackage ? [activePackage, ...metadataPackages] : metadataPackages;
  }, [activePackage, metadataPackages]);
  const selectedPackage = selectedPackageId
    ? (packages.find(({ id }) => id === selectedPackageId) ?? null)
    : null;
  const selectedParticipantDisplayList = selectedPackage
    ? getParticipantDisplayList(selectedPackage.participantIds, participantById)
    : [];

  const downloadSettings: UserDownloadInfo | null = useMemo(() => {
    if (!projectId || !selectedPeriod) return null;
    const participantIds = participantSelectionList
      .filter(({ selected }) => selected)
      .map(({ participant }) => participant.id);
    if (!participantIds.length) return null;

    const [periodStart, periodEnd] = selectedPeriod;
    return {
      projectId,
      participantIds,
      startDate: periodStart ? toUtcDateBoundary(periodStart) : undefined,
      endDate: periodEnd ? toUtcDateBoundary(periodEnd) : undefined,
    };
  }, [participantSelectionList, projectId, selectedPeriod]);

  const clearNextMetadataFetchTimeout = () => {
    if (metadataFetchTimeoutRef.current !== null) {
      clearTimeout(metadataFetchTimeoutRef.current);
      metadataFetchTimeoutRef.current = null;
    }
  };

  const onDownloadUpdate = (download: UserDownload) => {
    doUSER_DOWNLOAD_SET({ ...download });
  };

  const onDownloadError = (
    download: Required<Partial<UserDownload<UserDownloadResultError>>, "result">,
  ) => {
    console.error(`[DataDownload] ${JSON.stringify(download.result)}`);
    if (download.info) {
      doUSER_DOWNLOAD_SET({
        info: download.info,
        result: download.result,
        progress: download.progress ?? null,
        startedAt: download.startedAt ?? null,
      });
    }
    setShowDownloadErrorPopup(true);
  };

  const onDownloadTerminated = () => {
    doUSER_DOWNLOAD_DELETE({ projectId });
    clearNextMetadataFetchTimeout();
  };

  const performDownloadFlowMetadataFetch = (disableLoadingState = false) => {
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH({
      setRequestResult: setRequestResultOfProjectDataDownloadFlowMetadataFetch,
      payload: { projectId },
      disableLoadingState,
    });
  };

  const performDownloadFlowMetadataListFetch = (page = 0) => {
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_LIST_FETCH({
      payload: {
        projectId,
        page,
      },
      disableLoadingState: true,
    });
  };

  const performDownloadFlowRequest = (payload: UserDownloadInfo) => {
    if (isDownloadStartBlocked) return;
    setIsDownloadFlowRequestPending(true);
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_REQUEST({
      setRequestResult: setRequestResultOfProjectDataDownloadFlowRequest,
      payload,
      disableLoadingState: true,
    });
  };

  const performDownloadFlowCancel = () => {
    doREQUEST_PROJECT_DATA_DOWNLOAD_FLOW_CANCEL({
      setRequestResult: setRequestResultOfProjectDataDownloadFlowCancel,
      payload: { projectId },
    });
  };

  useEffect(() => {
    setHasCheckedActiveDownload(false);
    setIsDownloadFlowRequestPending(false);
    performDownloadFlowMetadataFetch();
    performDownloadFlowMetadataListFetch(0);
    return () => clearNextMetadataFetchTimeout();
  }, [projectId]);

  useEffect(() => {
    if (!errorUserDownloadResult) return;
    console.error(`[DataDownload] ${JSON.stringify(errorUserDownloadResult)}`);
    setShowDownloadErrorPopup(true);
  }, [errorUserDownloadResult]);

  useEffect(() => {
    if (activePackage && !hasAutoSelectedActiveRef.current) {
      setSelectedPackageId(activePackage.id);
      hasAutoSelectedActiveRef.current = true;
    }
    if (!activePackage) {
      hasAutoSelectedActiveRef.current = false;
    }
  }, [activePackage]);

  useEffect(() => {
    if (!selectedPackageId) return;
    if (!packages.some(({ id }) => id === selectedPackageId)) {
      setSelectedPackageId(null);
    }
  }, [packages, selectedPackageId]);

  useEffect(() => {
    if (!requestResultOfProjectDataDownloadFlowMetadataFetch) return;
    setHasCheckedActiveDownload(true);
    const { success, result } = requestResultOfProjectDataDownloadFlowMetadataFetch;
    if (success) {
      const {
        payload: { job, lastUpdate },
      } = result as ProjectDataDownloadFlowMetadataFetchResponsePayload;
      if (job?.isCancelled) {
        onDownloadTerminated();
        performDownloadFlowMetadataListFetch(0);
        return;
      }
      if (job?.state === JobState.Failed) {
        performDownloadFlowMetadataListFetch(0);
        onDownloadError({
          info: jobDataToUserDownloadInfo(job.data),
          result: {
            errorMessage: "project data download failed",
          },
        });
        return;
      }
      if (!job || job.state === JobState.Completed) {
        if (!lastUpdate) return;
        const {
          input,
          inputHash,
          url,
          createdAt,
          registeredAt,
          downloadedAt,
          failure,
          executionStart,
          isExpired,
          compressedSizeInBytes,
          uncompressedSizeInBytes,
          partsBreakdown,
          requester,
          id,
        } = lastUpdate;
        const info = jobDataToUserDownloadInfo({ input });

        if (failure) {
          performDownloadFlowMetadataListFetch(0);
          onDownloadError({
            info,
            result: {
              errorMessage: `project data download failed: ${failure.message}`,
            },
          });
          return;
        }
        if (!url || isExpired) {
          onDownloadTerminated();
          performDownloadFlowMetadataListFetch(0);
          setSelectedPackageId(id);
          return;
        }

        onDownloadUpdate({
          info,
          result: {
            inputHash,
            url,
            createdAt,
            registeredAt,
            downloadedAt,
            executionStart,
            compressedSizeInBytes,
            uncompressedSizeInBytes,
            partsBreakdown,
            requester,
          },
          progress: null,
          startedAt: registeredAt,
        });
        performDownloadFlowMetadataListFetch(0);
        setSelectedPackageId(id);
        return;
      }

      onDownloadUpdate({
        info: jobDataToUserDownloadInfo(job.data),
        startedAt: new Date(job.data.registeredAt),
        result: null,
        progress: job.progress ? { ...job.progress } : null,
      });
      metadataFetchTimeoutRef.current = setTimeout(
        () => performDownloadFlowMetadataFetch(true),
        METADATA_FETCH_INTERVAL,
      );
    } else {
      onDownloadError({
        info: userDownloadInfo ?? undefined,
        result: {
          errorMessage: "request project data download request failed",
        },
      });
    }
  }, [requestResultOfProjectDataDownloadFlowMetadataFetch]);

  useEffect(() => {
    if (!requestResultOfProjectDataDownloadFlowRequest) return;
    setIsDownloadFlowRequestPending(false);
    const { success, result } = requestResultOfProjectDataDownloadFlowRequest;
    if (success) {
      const {
        payload: { job },
      } = result as ProjectDataDownloadFlowRequestResponsePayload;
      const downloadInfo = jobDataToUserDownloadInfo(job.data);
      if (job.isCancelled) {
        onDownloadTerminated();
        return;
      }
      if (job.state === JobState.Failed) {
        onDownloadError({
          info: downloadInfo,
          result: {
            errorMessage: "project data download failed",
          },
        });
        return;
      }

      onDownloadUpdate({
        info: downloadInfo,
        startedAt: new Date(job.data.registeredAt),
        result: null,
        progress: job.progress ? { ...job.progress } : null,
      });
      setSelectedPackageId(ACTIVE_PACKAGE_ID);
      performDownloadFlowMetadataFetch();
      return;
    }

    onDownloadError({
      info: downloadSettings ?? undefined,
      result: { errorMessage: "request project data download flow request fail" },
    });
  }, [requestResultOfProjectDataDownloadFlowRequest]);

  useEffect(() => {
    if (!requestResultOfProjectDataDownloadFlowCancel) return;
    const { success } = requestResultOfProjectDataDownloadFlowCancel;
    if (success) {
      setShowCancelDialog(false);
      onDownloadTerminated();
      performDownloadFlowMetadataListFetch(0);
      setSelectedPackageId(null);
      return;
    }

    onDownloadError({
      info: userDownloadInfo ?? undefined,
      result: { errorMessage: "request project data download flow cancel failed" },
    });
  }, [requestResultOfProjectDataDownloadFlowCancel]);

  const handleTimeFrameSelect = (timeFrame: TimeFrameOption) => {
    setSelectedTimeFrame(timeFrame);
    if (timeFrame !== TimeFrameOption.specificPeriod) {
      setSelectedPeriod(relativeTimeFramePeriod[timeFrame]);
    }
  };

  const resetCreateDownloadForm = () => {
    setSelectedPackageId(null);
    setSelectedTimeFrame(TimeFrameOption.lastSevenDays);
    setSelectedPeriod(relativeTimeFramePeriod[TimeFrameOption.lastSevenDays]);
  };

  const handleCreatePackage = () => {
    if (!downloadSettings || isDownloadStartBlocked) return;
    performDownloadFlowRequest(downloadSettings);
  };

  const handleUpgradeCheck = () => {
    if (!canAccessPurchasedContent) {
      setShowUpgradePopup(true);
    }
  };

  return (
    <div
      css={{
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: 104,
        display: "flex",
        flexDirection: "column",
        width: "100vw",
        height: "100vh",
        minHeight: 600,
        background: tokens.bg,
        color: tokens.fg,
        fontFamily: tokens.font,
        fontSize: tokens.fontSize.base,
        lineHeight: 1.5,
        WebkitFontSmoothing: "antialiased",
        "@keyframes data-download-shimmer": {
          "0%, 100%": {
            opacity: 0.85,
          },
          "50%": {
            opacity: 1,
          },
        },
        "@keyframes data-download-spin": {
          to: {
            transform: "rotate(360deg)",
          },
        },
      }}
    >
      {showUpgradePopup ? (
        <UpgradeContactPop setDisplayUpgradeContactPop={(show) => setShowUpgradePopup(show)} />
      ) : null}
      {showDownloadErrorPopup ? (
        <DownloadErrorDialog onClose={() => setShowDownloadErrorPopup(false)} />
      ) : null}
      {showCancelDialog ? (
        <CancelBuildDialog
          onDismiss={() => setShowCancelDialog(false)}
          onConfirm={() => {
            setShowCancelDialog(false);
            performDownloadFlowCancel();
          }}
        />
      ) : null}
      <TopBar onClose={onClose} />
      <div
        css={{
          display: "grid",
          gridTemplateColumns: "380px 1fr",
          height: "calc(100vh - 56px)",
          minHeight: 600,
          minWidth: 0,
        }}
      >
        <PackageSidebar
          packages={packages}
          selectedPackageId={selectedPackageId}
          hasNextPage={metadataPage?.next !== null && metadataPage?.next !== undefined}
          nextPage={metadataPage?.next}
          disableCreateNewDownload={isDownloadStartBlocked}
          onSelectNew={resetCreateDownloadForm}
          onSelectPackage={(id) => setSelectedPackageId(id)}
          onCancelPackage={() => setShowCancelDialog(true)}
          onLoadMore={(page) => performDownloadFlowMetadataListFetch(page)}
        />
        <main
          css={{
            overflowY: "auto",
            padding: "32px 40px",
            minHeight: 0,
            "::-webkit-scrollbar": {
              width: 8,
            },
            "::-webkit-scrollbar-thumb": {
              background: tokens.scrollbarThumb,
              borderRadius: 4,
            },
          }}
        >
          {selectedPackage ? (
            selectedPackage.status === "zipping" ? (
              <ZippingDetailView
                pkg={selectedPackage}
                participantDisplayList={selectedParticipantDisplayList}
                onCancel={() => setShowCancelDialog(true)}
              />
            ) : (
              <PackageDetailView
                pkg={selectedPackage}
                participantDisplayList={selectedParticipantDisplayList}
              />
            )
          ) : (
            <CreateDownloadView
              participantSelectionList={participantSelectionList}
              canAccessPurchasedContent={canAccessPurchasedContent}
              selectedTimeFrame={selectedTimeFrame}
              selectedPeriod={selectedPeriod}
              relativeTimeFramePeriod={relativeTimeFramePeriod}
              canCreatePackage={!!downloadSettings && !isDownloadStartBlocked}
              onParticipantSelectionChange={(changed) => setParticipantSelectionList(changed)}
              onTimeFrameSelect={handleTimeFrameSelect}
              onPeriodChange={(period) => setSelectedPeriod(period)}
              onCreatePackage={handleCreatePackage}
              onUpgradeCheck={handleUpgradeCheck}
            />
          )}
        </main>
      </div>
    </div>
  );
};
